<template>
    <div
        class="product-image-box"
        :style="{ aspectRatio }"
        :class="{ 'slideshow-active': isHovering }"
        @mouseenter="startCarousel"
        @mouseleave="stopCarousel"
    >
        <img
            v-lazy-img="buildImageBinding(mainImage)"
            :alt="imageAlt"
            class="main-image"
        />

        <img
            v-if="images.length"
            v-show="isHovering"
            v-lazy-img="buildImageBinding(slideImageA)"
            class="slide-image"
            :class="{ active: activeIndex === 0 }"
        />

        <img
            v-if="images.length"
            v-show="isHovering"
            v-lazy-img="buildImageBinding(slideImageB)"
            class="slide-image"
            :class="{ active: activeIndex === 1 }"
        />

        <slot></slot>
    </div>
</template>

<script setup lang="ts">
    import { onBeforeUnmount, ref, watch } from 'vue';
    import {
        IMAGE_FETCH_PRIORITY,
        IMAGE_LOADING,
        type LazyImageBinding,
    } from '@/types/components/image-loading.types';

    const props = withDefaults(
        defineProps<{
            mainImage: string;
            images: string[];
            imageAlt?: string;
            disabled?: boolean;
            aspectRatio?: string;
            lazyTag?: string;
            cdnPrefixes?: string[];
            loading?: IMAGE_LOADING;
            fetchPriority?: IMAGE_FETCH_PRIORITY;
        }>(),
        {
            imageAlt: '',
            disabled: false,
            aspectRatio: '1 / 1',
            lazyTag: '',
            cdnPrefixes: () => [],
            loading: IMAGE_LOADING.EAGER,
            fetchPriority: IMAGE_FETCH_PRIORITY.AUTO,
        }
    );

    const processedImages = ref<string[]>([]);
    const timer = ref<ReturnType<typeof setInterval> | null>(null);
    const index = ref(0);
    const activeIndex = ref(0);
    const slideImageA = ref('');
    const slideImageB = ref('');
    const isHovering = ref(false);
    const externalFlag = ref(false);

    // ----- methods -----
    // 建立 v-lazy-img 綁定資料，圖片格式與快取策略由 LazyLoader tag 決定
    const buildImageBinding = (src: string): LazyImageBinding => {
        const binding: LazyImageBinding = {
            src,
            tag: props.lazyTag,
            loading: props.loading,
            fetchPriority: props.fetchPriority,
        };

        if (Array.isArray(props.cdnPrefixes) && props.cdnPrefixes.length) {
            binding.cdnPrefixes = props.cdnPrefixes;
        }

        return binding;
    };

    // 預處理圖片
    const processImages = (images: string[]): void => {
        processedImages.value = Array.isArray(images)
            ? images.filter((imagePath): imagePath is string =>
                  Boolean(imagePath)
              )
            : [];
    };

    // 圖片是否要輪播
    const checkCarousel = (): boolean => {
        if (props.disabled) return false;
        return processedImages.value.length > 0;
    };

    // 尋找下一張輪播圖片
    const next = (): void => {
        if (!processedImages.value.length) return;

        index.value = (index.value + 1) % processedImages.value.length;
        const nextImage = processedImages.value[index.value];

        if (activeIndex.value === 0) {
            slideImageB.value = nextImage;
            activeIndex.value = 1;
        } else {
            slideImageA.value = nextImage;
            activeIndex.value = 0;
        }
    };

    // 定時輪播圖片
    const startCarousel = (): void => {
        if (!checkCarousel()) return;

        // 如果已有事件動作，不再觸發
        if (timer.value || externalFlag.value) return;

        isHovering.value = true;
        index.value = 0;

        slideImageA.value = processedImages.value[0];
        slideImageB.value =
            processedImages.value[1] || processedImages.value[0];
        activeIndex.value = 0;

        timer.value = setInterval(next, 1600);
    };

    const stopCarousel = (): void => {
        if (externalFlag.value) return;

        if (timer.value) {
            clearInterval(timer.value);
            timer.value = null;
        }

        isHovering.value = false;
    };

    // 外層呼叫開始輪播
    const externalStartTrigger = (): void => {
        if (!checkCarousel()) return;
        if (timer.value) return;

        externalFlag.value = true;
        isHovering.value = true;
        index.value = 0;

        slideImageA.value = processedImages.value[0];
        slideImageB.value =
            processedImages.value[1] || processedImages.value[0];

        activeIndex.value = 0;
        timer.value = setInterval(next, 1600);
    };

    const externalStopTrigger = (): void => {
        externalFlag.value = false;
        stopCarousel();
    };

    defineExpose({
        externalStartTrigger,
        externalStopTrigger,
    });

    // ----- watch -----
    watch(
        () => props.images,
        (newImages) => {
            processImages(newImages);
        },
        {
            immediate: true,
            deep: true,
        }
    );

    // ----- lifecycle -----
    onBeforeUnmount(() => {
        if (timer.value) {
            clearInterval(timer.value);
            timer.value = null;
        }
    });
</script>
